import { __awaiter } from "tslib";
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { clearCombinedToken as clearAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getCurrentEvoInstance, getLicenseToken, setAvailableEvoInstances, setCentralInstances, setLicenseToken, } from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { EntitlementsApiClient } from '../EntitlementsApiClient';
export const CentralEntitlementsApi = EntitlementsApiClient.injectEndpoints({
    endpoints: (build) => ({
        loadCentralInstances: build.query({
            queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield baseQuery('v1/licenses/id/central/servers');
                    if (result.error) {
                        return { error: result.error };
                    }
                    const data = result.data;
                    const instances = {
                        servers: [],
                    };
                    data.servers.forEach((apiInstance) => {
                        const convertedInstance = {
                            displayName: apiInstance.display_name,
                            host: apiInstance.host,
                            port: apiInstance.port,
                            instanceUUID: apiInstance.instance_id,
                            entitlementId: apiInstance.entitlement_id,
                            name: apiInstance.name,
                            organizationName: apiInstance.organization_name,
                            validTo: apiInstance.valid_to,
                        };
                        instances.servers.push(convertedInstance);
                    });
                    setCentralInstances(instances);
                    return { data: instances };
                });
            },
        }),
    }),
});
const EntitlementApiWithTags = EntitlementsApiClient.enhanceEndpoints({ addTagTypes: ['Token'] });
const tokenMutex = new Mutex();
export const EntitlementsApi = EntitlementApiWithTags.injectEndpoints({
    endpoints: (build) => ({
        loadLicenseOrgs: build.query({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ service }, _queryApi, _extraOptions, baseQuery) {
                    const discoveryResponse = yield baseQuery({
                        url: '/evo/identity/v1/discovery',
                        params: { service },
                    });
                    if (discoveryResponse.error) {
                        const { status } = discoveryResponse.error;
                        if (status && typeof status === 'number' && status >= 400) {
                            clearAccessToken();
                        }
                        return { error: discoveryResponse.error };
                    }
                    const discovery = discoveryResponse.data;
                    if (!discovery || !discovery.discovery) {
                        return { error: { status: 'CUSTOM_ERROR', error: 'Error: No licenses' } };
                    }
                    if (!discovery.discovery.service_access.length) {
                        setAvailableEvoInstances([]);
                        return { data: discovery.discovery };
                    }
                    const hubs = new Map();
                    for (const hub of discovery.discovery.hubs) {
                        hubs.set(hub.code, hub);
                    }
                    const orgs = new Map();
                    for (const org of discovery.discovery.organizations) {
                        orgs.set(org.id, org);
                    }
                    const availableEvoInstances = [];
                    for (const sa of discovery.discovery.service_access) {
                        const hub = hubs.get(sa.hub_code);
                        const org = orgs.get(sa.org_id);
                        if (sa.services.includes(service) && hub && org) {
                            availableEvoInstances.push({ org, hub });
                        }
                    }
                    setAvailableEvoInstances(availableEvoInstances);
                    if (availableEvoInstances.length === 0) {
                        return {
                            error: {
                                status: 'CUSTOM_ERROR',
                                error: `Error: No valid org/hub for ${service}`,
                            },
                        };
                    }
                    return { data: discovery.discovery };
                });
            },
        }),
        loadLicenseToken: build.query({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ service }, _queryApi, _extraOptions, baseQuery) {
                    const { org, hub } = getCurrentEvoInstance();
                    if (!org || !hub) {
                        return { error: { status: 500, data: 'No instance to get license for' } };
                    }
                    yield tokenMutex.waitForUnlock();
                    const release = yield tokenMutex.acquire();
                    let resp;
                    try {
                        resp = (yield baseQuery({
                            url: '/evo/identity/v1/token',
                            params: {
                                org_id: org.id,
                                service,
                                hub: hub.code,
                            },
                            responseHandler: (response) => response.text().then((s) => {
                                const token = s.trimStart().trimEnd();
                                if (response.status === 200) {
                                    setLicenseToken(token);
                                }
                                return token;
                            }),
                        }));
                    }
                    finally {
                        release();
                    }
                    return resp;
                });
            },
            keepUnusedDataFor: 1200,
            providesTags: ['Token'],
        }),
    }),
});
export function createLicensedBaseQuery(url, service) {
    return (args, WebApi, extraOptions) => __awaiter(this, void 0, void 0, function* () {
        const baseUrl = url();
        if (baseUrl === null) {
            return { error: { status: 500, data: 'Not enough information to generate url' } };
        }
        const rawBaseQuery = fetchBaseQuery({
            baseUrl,
            prepareHeaders: (headers) => {
                const token = getLicenseToken();
                if (token) {
                    headers.set('authorization', `Bearer ${token}`);
                }
                return headers;
            },
        });
        yield tokenMutex.waitForUnlock();
        let result = yield rawBaseQuery(args, WebApi, extraOptions);
        if (result.error && result.error.status === 401) {
            const { org, hub } = getCurrentEvoInstance();
            if (!org || !hub) {
                return result;
            }
            if (!tokenMutex.isLocked()) {
                const originalToken = getLicenseToken();
                yield WebApi.dispatch(EntitlementsApi.endpoints.loadLicenseToken.initiate({ service }, { subscribe: true, forceRefetch: true }));
                const newToken = getLicenseToken();
                if (originalToken !== newToken) {
                    result = yield rawBaseQuery(args, WebApi, extraOptions);
                }
            }
            else {
                yield tokenMutex.waitForUnlock();
                result = yield rawBaseQuery(args, WebApi, extraOptions);
            }
        }
        return result;
    });
}
export const { useLoadCentralInstancesQuery } = CentralEntitlementsApi;
export const { useLoadLicenseOrgsQuery, useLoadLicenseTokenQuery } = EntitlementsApi;
//# sourceMappingURL=EntitlementsAPI.js.map